import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const sqButtonVariants = cva(
  "relative inline-flex items-center justify-center gap-2 whitespace-nowrap squircle transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
  {
    variants: {
      variant: {
        default: "text-white",
        fill: "text-white",
        outline: "text-brand-primary-800",
        icon: "text-white",
      },
      size: {
        default:
          "h-9 px-[16px] py-[10px] text-[12px] font-normal leading-[19.157px] tracking-[0.24px]",
        sm: "h-7 px-[8px] py-[4px] text-[12px] gap-[6px]",
        md: "h-8 px-[12px] py-[6px] text-[12px] gap-[8px]",
        lg: "h-9 px-[16px] py-[10px] text-[14px] gap-[12px]",
        xl: "h-10 px-[20px] py-[12px] text-[16px] gap-[14px]",
      },
      shadow: {
        none: "",
        sm: "drop-shadow-sm",
        md: "drop-shadow-md",
        lg: "drop-shadow-lg",
        xl: "drop-shadow-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shadow: "none",
    },
    compoundVariants: [
      {
        variant: "icon",
        size: "sm",
        class: "h-7 w-7 px-0 py-0 gap-0",
      },
      {
        variant: "icon",
        size: "md",
        class: "h-8 w-8 px-0 py-0 gap-0",
      },
      {
        variant: "icon",
        size: "lg",
        class: "h-9 w-9 px-0 py-0 gap-0",
      },
      {
        variant: "icon",
        size: "xl",
        class: "h-10 w-10 px-0 py-0 gap-0",
      },
      {
        variant: "icon",
        size: "default",
        class: "h-9 w-9 px-0 py-0 gap-0",
      },
    ],
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof sqButtonVariants> {
  asChild?: boolean;
  bgColor?: string;
  outlineColor?: string;
  hoverBgColor?: string;
  hoverOutlineColor?: string;
  radius?: number;
  hasOutline?: boolean;
}

const SqButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      shadow,
      asChild = false,
      bgColor,
      outlineColor,
      hoverBgColor,
      hoverOutlineColor,
      radius = 8,
      hasOutline = false,
      style,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    // Build base classes - NO defaults, only when props are provided
    const baseClasses = cn(sqButtonVariants({ variant, size, shadow }));

    // Build squircle classes with fallbacks - only when props are provided
    const squircleClasses = cn(
      // Base squircle class and smooth corners
      "squircle squircle-smooth-[1]",

      // Radius - only when provided
      radius && "squircle-radius-[var(--sq-radius)] rounded-[var(--sq-radius)]",

      // Background colors - only when provided
      bgColor && "squircle-fill-[var(--sq-fill)]",
      hoverBgColor && "hover:squircle-fill-[var(--sq-hover-fill)]",

      // Outline - only when explicitly enabled
      hasOutline &&
        "relative before:absolute before:inset-0 before:squircle before:pointer-events-none",
      hasOutline && "before:squircle-outline-[1px]",
      hasOutline && outlineColor && "before:squircle-outline-color-[var(--sq-outline)]",
      hasOutline && !outlineColor && "before:squircle-outline-color-[#E7E8E9]",
      hasOutline &&
        hoverOutlineColor &&
        "hover:before:squircle-outline-color-[var(--sq-hover-outline)]",
    );

    // Apply default variant styles only when no custom colors are provided
    const defaultVariantClasses = cn(
      // Default variant styles - only when no custom colors
      !bgColor &&
        !hoverBgColor &&
        variant === "default" &&
        "squircle-fill-[var(--sq-default-fill)] hover:squircle-fill-[var(--sq-default-hover-fill)]",
      !bgColor &&
        !hoverBgColor &&
        variant === "fill" &&
        "squircle-fill-[var(--sq-default-fill)] hover:squircle-fill-[var(--sq-default-hover-fill)]",
      !bgColor &&
        !hoverBgColor &&
        variant === "outline" &&
        "squircle-fill-[var(--sq-white)] hover:squircle-fill-[var(--sq-hover-gray)] before:absolute before:inset-0 before:squircle before:squircle-radius-[var(--sq-radius)] before:squircle-outline-[var(--sq-outline-width)] before:squircle-outline-color-[var(--sq-outline-color)] before:pointer-events-none",
      !bgColor &&
        !hoverBgColor &&
        variant === "icon" &&
        "squircle-fill-[var(--sq-default-fill)] hover:squircle-fill-[var(--sq-default-hover-fill)]",
    );

    // Set CSS variables for squircle classes
    const squircleStyle = {
      ...(style || {}),
      "--sq-radius": `${radius}px`,
      "--sq-outline-width": "1px",
      "--sq-outline-color": "#E7E8E9",
      "--sq-white": "white",
      "--sq-hover-gray": "#FBFBFB",
      "--sq-default-fill": "#FF4500",
      "--sq-default-hover-fill": "#F42100",
      ...(bgColor && { "--sq-fill": bgColor }),
      ...(hoverBgColor && { "--sq-hover-fill": hoverBgColor }),
      ...(outlineColor && { "--sq-outline": outlineColor }),
      ...(hoverOutlineColor && { "--sq-hover-outline": hoverOutlineColor }),
    } as React.CSSProperties;

    return (
      <Comp
        className={cn(baseClasses, squircleClasses, defaultVariantClasses, className)}
        ref={ref}
        style={squircleStyle}
        {...props}
      >
        {props.children}
      </Comp>
    );
  },
);
SqButton.displayName = "Button";

export { SqButton, sqButtonVariants };
